<template>
    <div v-if="data && data.length" class="uik-shares">
        <component
            :is="item.tooltip ? 'UikTooltip' : 'div'"
            v-for="(item, index) in data"
            :key="'share-' + index"
            class="uik-shares__share-wrapper"
            :class="`uik-shares__share-wrapper--${getItemColor(item)}`"
            :text="item.tooltip || null"
            :style="`width: ${getItemWidth(item)}%`"
        >
            <div class="uik-shares__share"/>
        </component>
    </div>
</template>

<script>
export default {
    name: "UikShares",
    props: {
        /**
         * Shares data
         *
         * Can be array of strings, numbers or objects with 'data' property.
         *
         * Examples:
         * - [20, "40", "60", 80]
         * - [ { data: 20 }, { data: "40" }, { data: "60" }, { data: 80 } ]
         */
        data: {
            type: Array,
            default () { return [] }
        },
        /**
         * Universal shares color
         * If defined, overrides specific colors
         */
        color: {
            type: String,
            default: "",
            validator (value) {
                return [
                    "",
                    "primary",
                    "secondary",
                    "success",
                    "warning",
                    "error",
                    "red",
                    "orange",
                    "yellow",
                    "lime",
                    "green",
                    "cyan",
                    "blue",
                    "purple",
                    "pink",
                    "gray"
                ].indexOf(value) !== -1
            }
        }
    },
    methods: {
        /**
         * Gets item's or universal color
         *
         * @returns {String} color
         */
        getItemColor (item) {
            if (this.color) return this.color
            if (typeof item === "object" && item.color) return item.color

            return "primary" // default color
        },
        /**
         * Gets item's width
         *
         * @return {Number} width in percentage
         */
        getItemWidth (item) {
            const sum = this.getSum()
            const value = this.getItemValue(item)

            return value * 100 / sum
        },
        /**
         * Gets sum of all values
         *
         * @returns {Number}
         */
        getSum () {
            let sum = 0

            for (let i = 0; i < this.data.length; i++) {
                const value = this.getItemValue(this.data[i])
                sum += value
            }

            return sum
        },
        /**
         * Gets item's value
         *
         * @returns {Number}
         */
        getItemValue (item) {
            let value = item
            if (typeof item === "object") value = item.data

            value = parseFloat(value)
            if (isNaN(value)) return 0

            return value
        }
    }
}
</script>

<style lang="scss">
.uik-shares {
    display: flex;
    flex-flow: row nowrap;
    justify-content: flex-start;
    align-items: stretch;
    width: 100%;
    min-width: 120px;

    .uik-shares__share-wrapper {
        padding: 5px 0;

        .uik-shares__share {
            width: 100%;
            height: 6px;
            transition: all 0.1s;

            &:hover {
                transition: all 0.1s, background-color 0s;
            }
        }

        @each $name, $color in $colors {
            &--#{"" + $name} {
                .uik-shares__share {
                    background: $color;
                }

                &:hover {
                    .uik-shares__share {
                        background: lighten($color, 10%);
                    }
                }
            }
        }

        @each $name, $color in $projectColors {
            &--#{"" + $name} {
                .uik-shares__share {
                    background: $color;
                }

                &:hover {
                    .uik-shares__share {
                        background: lighten($color, 10%);
                    }
                }
            }
        }

        &:first-child {
            .uik-shares__share {
                border-radius: 99px 0 0 99px;
            }
        }

        &:last-child {
            .uik-shares__share {
                border-radius: 0 99px 99px 0;
            }
        }

        & + .uik-shares__share-wrapper {
            margin-left: 3px;
        }
    }
}
</style>
